<template>
  <header>
    <div class="logo-title">
      <img src="@/assets/VivaVegasLogo.png" alt="Vivavegas Logo" class="logo" />
    </div>
    <nav>
      <router-link to="/games" class="nav-link">Games</router-link>
      <router-link v-if="isLoggedIn" to="/purchase" class="nav-link"
        >Purchase</router-link
      >
      <router-link v-if="isLoggedIn" to="/withdraw" class="nav-link"
        >Withdraw</router-link
      >
    </nav>

    <div class="right-section">
      <div v-if="!isLoggedIn" class="auth-links">
        <router-link to="/login" class="nav-link">Login</router-link>
        <router-link to="/register" class="nav-link">Register</router-link>
      </div>
      <div v-else class="user-info">
        <div class="username-display">{{ currentUsername }}</div>
        <div class="balance">
          <div class="balance-item">
            <img
              src="@/assets/gold-coin.png"
              alt="Gold"
              class="currency-icon"
            />
            <span
              :class="{
                'balance-updating': goldBalanceUpdating,
                'balance-decreasing': goldBalanceDecreasing,
              }"
              class="balance-value"
              >{{ displayGoldBalance }}</span
            >
          </div>
          <div class="balance-item">
            <img
              src="@/assets/sweep-coin.png"
              alt="Sweeps"
              class="currency-icon"
            />
            <span
              :class="{
                'balance-updating': sweepsBalanceUpdating,
                'balance-decreasing': sweepsBalanceDecreasing,
              }"
              class="balance-value"
              >{{ displaySweepsBalance }}</span
            >
          </div>
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>
      <DarkModeToggle />
    </div>
  </header>
</template>

<script>
import { mapState } from "vuex";
import DarkModeToggle from "./DarkModeToggle.vue";

export default {
  name: "Header",
  components: {
    DarkModeToggle,
  },
  data() {
    return {
      displayGoldBalance: 0,
      displaySweepsBalance: 0,
      goldBalanceUpdating: false,
      sweepsBalanceUpdating: false,
      goldBalanceDecreasing: false,
      sweepsBalanceDecreasing: false,
    };
  },
  computed: {
    ...mapState(["isLoggedIn", "goldCoinsBalance", "sweepsCoinsBalance"]),
    currentUsername() {
      // Only show username if actually logged in
      if (!this.isLoggedIn) {
        return "Guest";
      }

      try {
        const userData = localStorage.getItem("demoUserData");
        if (userData) {
          const parsedData = JSON.parse(userData);
          return parsedData.username || "Guest";
        }
      } catch (error) {
        console.error("Error getting username:", error);
      }
      return "Guest";
    },
  },
  watch: {
    goldCoinsBalance: {
      handler(newValue, oldValue) {
        if (oldValue !== undefined) {
          this.animateBalance("gold", oldValue, newValue);
        } else {
          this.displayGoldBalance = newValue;
        }
      },
      immediate: true,
    },
    sweepsCoinsBalance: {
      handler(newValue, oldValue) {
        if (oldValue !== undefined) {
          this.animateBalance("sweeps", oldValue, newValue);
        } else {
          this.displaySweepsBalance = newValue;
        }
      },
      immediate: true,
    },
  },
  methods: {
    logout() {
      this.$store.dispatch("logout");
    },
    animateBalance(type, fromValue, toValue) {
      const isGold = type === "gold";
      const duration = 800;
      const startTime = performance.now();
      const isDecreasing = toValue < fromValue;

      // Set updating state using computed property names
      this[`${type}BalanceUpdating`] = true;
      this[`${type}BalanceDecreasing`] = isDecreasing;

      const animate = (currentTime) => {
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out
        const currentValue = Math.round(
          fromValue + (toValue - fromValue) * eased
        );

        // Update display value using computed property name
        this[`display${isGold ? "Gold" : "Sweeps"}Balance`] = currentValue;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Reset states after animation
          setTimeout(() => {
            this[`${type}BalanceUpdating`] = false;
            this[`${type}BalanceDecreasing`] = false;
          }, 200);
        }
      };

      requestAnimationFrame(animate);
    },
  },
  created() {
    // Initialize display values immediately
    this.displayGoldBalance = this.goldCoinsBalance || 0;
    this.displaySweepsBalance = this.sweepsCoinsBalance || 0;
  },
};
</script>

<style scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: linear-gradient(
    to right,
    var(--header-gradient-start),
    var(--header-gradient-end)
  );
  box-shadow: 0 -5px 20px var(--accent-color);
  color: white;
  flex-wrap: wrap;
  transition: background 0.3s ease;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-title {
  display: flex;
  align-items: center;
  color: #cc55e4;
}

.logo {
  width: 100px;
  margin-right: 10px;
}

nav {
  display: flex;
  gap: 10px;
}
.nav-link {
  color: var(--text-light);
  text-decoration: none;
  padding: 0.5rem 1rem;
  margin: 0 0.5rem;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.nav-link:hover {
  background-color: var(--secondary-color);
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.username-display {
  color: var(--accent-color);
  font-size: 0.9rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.balance {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logout-btn {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
}

.logout-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
}

.header-link {
  color: white;
}

/* Enhanced mobile responsive styles */
@media (max-width: 768px) {
  header {
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
    align-items: center;
  }

  .logo-title {
    order: 1;
  }

  .logo {
    width: 80px;
    margin-right: 8px;
  }

  nav {
    order: 2;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }

  .nav-link {
    padding: 0.4rem 0.8rem;
    margin: 0 0.2rem;
    font-size: 0.9rem;
  }

  .right-section {
    order: 3;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    width: 100%;
  }

  .auth-links {
    display: flex;
    gap: 8px;
  }

  .user-info {
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 400px;
  }

  .username-display {
    font-size: 0.85rem;
    text-align: center;
  }

  .balance {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }

  .balance-item {
    padding: 0.4rem 0.8rem;
    min-width: 80px;
  }

  .currency-icon {
    width: 20px;
    height: 20px;
    margin-right: 0.4rem;
  }

  .balance-value {
    font-size: 0.9rem;
  }

  .logout-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    margin-top: 0.5rem;
  }
}

@media (max-width: 480px) {
  header {
    padding: 0.8rem;
    gap: 0.8rem;
  }

  .logo {
    width: 70px;
    margin-right: 6px;
  }

  nav {
    gap: 6px;
  }

  .nav-link {
    padding: 0.35rem 0.7rem;
    margin: 0 0.1rem;
    font-size: 0.85rem;
  }

  .auth-links {
    gap: 6px;
  }

  .user-info {
    gap: 6px;
    max-width: 100%;
  }

  .username-display {
    font-size: 0.8rem;
  }

  .balance {
    gap: 6px;
    width: 100%;
  }

  .balance-item {
    padding: 0.35rem 0.6rem;
    min-width: 70px;
    flex: 1;
    justify-content: center;
  }

  .currency-icon {
    width: 18px;
    height: 18px;
    margin-right: 0.3rem;
  }

  .balance-value {
    font-size: 0.85rem;
  }

  .logout-btn {
    padding: 0.35rem 0.7rem;
    font-size: 0.75rem;
    width: 100%;
    max-width: 120px;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  header {
    padding: 0.6rem;
    gap: 0.6rem;
  }

  .logo {
    width: 60px;
    margin-right: 4px;
  }

  .nav-link {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }

  .balance {
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }

  .balance-item {
    width: 100%;
    max-width: 150px;
    justify-content: center;
  }

  .logout-btn {
    width: 100%;
    max-width: 150px;
    margin-top: 0.3rem;
  }
}

/* Landscape mobile orientation */
@media (max-width: 768px) and (orientation: landscape) {
  header {
    flex-direction: row;
    padding: 0.8rem 1rem;
    gap: 1rem;
    align-items: center;
  }

  .logo-title {
    order: 1;
    flex-shrink: 0;
  }

  .logo {
    width: 60px;
  }

  nav {
    order: 2;
    flex: 1;
    justify-content: center;
    gap: 6px;
  }

  .nav-link {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }

  .right-section {
    order: 3;
    flex-direction: row;
    gap: 8px;
    flex-shrink: 0;
    width: auto;
  }

  .auth-links,
  .user-info {
    flex-shrink: 0;
  }

  .user-info {
    align-items: flex-end;
    gap: 4px;
    width: auto;
  }

  .username-display {
    font-size: 0.75rem;
  }

  .balance {
    gap: 4px;
    flex-wrap: nowrap;
  }

  .balance-item {
    padding: 0.2rem 0.4rem;
    min-width: 60px;
  }

  .currency-icon {
    width: 16px;
    height: 16px;
  }

  .balance-value {
    font-size: 0.75rem;
  }

  .logout-btn {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
    margin-top: 0.2rem;
  }
}

.balance-display {
  display: flex;
  gap: 1rem;
}

.balance-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.currency-icon {
  width: 24px;
  height: 24px;
  margin-right: 0.5rem;
}

.balance-value {
  transition: all 0.3s ease;
  font-weight: bold;
}

.balance-updating {
  transform: scale(1.2);
  color: var(--accent-color);
  text-shadow: 0 0 10px var(--accent-color);
  animation: pulse 0.8s ease-in-out;
}

.balance-updating.balance-decreasing {
  color: #ff4444;
  text-shadow: 0 0 10px #ff4444;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1.2);
  }
}
</style>
